import { CheckOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Radio,
  Result,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Modal from '@/components/Modal';
import {
  BRANCH_ALL_ACCESS_PERMISSION,
  USER_ROLE_TYPE,
  USER_STATUS,
  USER_TYPE,
} from '@/constants';
import { useAddAdminUser } from '@/hooks/mutations/admin';
import { useAdminUserRoles } from '@/hooks/queries/admin';
import type { AddAdminUserRequest, AddAdminUserResponse } from '@/types/admin';
import { getQueryKey } from '@/utils/queryKeys';
import {
  copyToClipboard,
  generatePassword,
  getApiErrorMessages,
} from '@/utils/user';

import BranchTagSelect from './BranchTagSelect';

type FormValues = AddAdminUserRequest;

interface CreatedAccount {
  id: number;
  name: string;
  email: string;
  password: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onViewDetail?: (userId: number) => void;
}

const PHONE_PATTERN = /^[0-9]{3}-[0-9]{3,4}-[0-9]{4}$/;

/** 숫자만 남기고 010-1234-5678 형태로 하이픈을 넣는다 */
const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length < 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

/** 복사에 성공하면 true */
const copy = async (text: string, label: string) => {
  try {
    await copyToClipboard(text);
    message.success(`${label}을(를) 복사했어요`);
    return true;
  } catch {
    message.error('복사하지 못했어요. 직접 선택해서 복사해주세요.');
    return false;
  }
};

const buildAccountGuide = ({ email, password }: CreatedAccount) =>
  [
    '[다락 파트너스] 계정 안내',
    `로그인 주소: ${window.location.origin}${PATH.LOGIN}`,
    `이메일: ${email}`,
    `임시 비밀번호: ${password}`,
    '로그인 후 [정보수정]에서 비밀번호를 변경해주세요.',
  ].join('\n');

const CreateUserModal = ({ isOpen, onClose, onViewDetail }: Props) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [created, setCreated] = useState<CreatedAccount | null>(null);
  // 계정 안내 문구를 복사해야 완료 모달을 닫을 수 있다 (비밀번호는 이 창에서만 볼 수 있음)
  const [guideCopied, setGuideCopied] = useState(false);
  const [guideCopyFailed, setGuideCopyFailed] = useState(false);

  const roles = useAdminUserRoles({ enabled: isOpen });
  const userType = Form.useWatch('userType', form);
  const userRoleId = Form.useWatch('userRoleId', form);
  const password = Form.useWatch('password', form);

  const roleList = useMemo(() => roles.data?.results ?? [], [roles.data]);
  const selectedRole = roleList.find((role) => role.id === userRoleId);
  // 이 역할은 user_branch 매핑과 무관하게 모든 지점에 접근한다 (백엔드 User.has_all_branch_access 와 같은 판정)
  const roleHasAllBranchAccess =
    !!selectedRole &&
    (selectedRole.isSuperRole ||
      selectedRole.permissions.includes(BRANCH_ALL_ACCESS_PERMISSION));

  const addAdminUser = useAddAdminUser({
    onSuccess: (response: AddAdminUserResponse, variables) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getUsers'),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getUserRoles'),
      });
      setCreated({
        id: response.id,
        name: variables.name,
        email: variables.email,
        password: variables.password,
      });
    },
    onError: (error) => {
      const { message: errorMessage, fields } = getApiErrorMessages(error);
      const formFields = Object.entries(fields)
        .filter(([name]) => form.getFieldInstance(name as keyof FormValues))
        .map(([name, text]) => ({
          name: name as keyof FormValues,
          errors: [text],
        }));

      if (formFields.length > 0) {
        form.setFields(formFields);
      }
      message.error(errorMessage);
    },
  });

  useEffect(() => {
    if (isOpen) {
      setCreated(null);
      setGuideCopied(false);
      setGuideCopyFailed(false);
      form.resetFields();
      form.setFieldsValue({
        userType: 'PARTNER',
        userStatus: 'ACTIVE',
        branchIds: [],
        password: generatePassword(),
      });
    }
  }, [isOpen]);

  // 유형을 바꾸면 그 유형의 역할을 기본으로 고른다 (이미 맞는 유형의 역할을 골랐으면 유지)
  useEffect(() => {
    if (!isOpen || !userType || roleList.length === 0) return;

    const expectedTypes =
      userType === 'ADMIN' ? ['ADMIN', 'SUPER_ROLE'] : ['PARTNER'];
    const current = roleList.find(
      (role) => role.id === form.getFieldValue('userRoleId')
    );

    if (current && expectedTypes.includes(current.type)) return;

    const fallback =
      roleList.find((role) => role.type === expectedTypes[0]) ??
      roleList.find((role) => expectedTypes.includes(role.type));

    form.setFieldValue('userRoleId', fallback?.id);
  }, [isOpen, userType, roleList]);

  const roleOptions = useMemo(
    () =>
      roleList.map((role) => ({
        value: role.id,
        label: role.name,
        role,
      })),
    [roleList]
  );

  const handleClose = () => {
    if (addAdminUser.isPending) return;
    onClose();
  };

  if (created) {
    const guide = buildAccountGuide(created);
    const closeCreated = (afterClose?: () => void) => {
      if (!guideCopied) {
        message.warning('계정 안내 문구를 복사해야 닫을 수 있어요');
        return;
      }
      afterClose?.();
      onClose();
    };

    return (
      <Modal
        open={isOpen}
        width={520}
        maskClosable={false}
        // X 버튼 · ESC 도 onCancel 로 들어온다
        onCancel={() => closeCreated()}
        title="유저 생성 완료"
        footer={[
          onViewDetail && (
            <Button
              key="detail"
              disabled={!guideCopied}
              onClick={() => closeCreated(() => onViewDetail(created.id))}
            >
              상세 보기
            </Button>
          ),
          <Button
            key="close"
            type="primary"
            disabled={!guideCopied}
            onClick={() => closeCreated()}
          >
            닫기
          </Button>,
        ]}
      >
        <div css={tw`[.ant-result]:p-[16px 0]`}>
          <Result
            status="success"
            title="계정이 생성됐어요"
            subTitle="아래 정보를 파트너에게 전달해주세요."
          />
        </div>

        <Descriptions
          bordered
          column={1}
          size="small"
          items={[
            { key: 'name', label: '이름', children: created.name },
            {
              key: 'email',
              label: '이메일',
              children: (
                <div
                  css={tw`flex flex-row items-center justify-between w-full`}
                >
                  <Typography.Text>{created.email}</Typography.Text>
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copy(created.email, '이메일')}
                  />
                </div>
              ),
            },
            {
              key: 'password',
              label: '임시 비밀번호',
              children: (
                <div
                  css={tw`flex flex-row items-center justify-between w-full`}
                >
                  <Typography.Text code css={tw`text-[15px]`}>
                    {created.password}
                  </Typography.Text>
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copy(created.password, '비밀번호')}
                  />
                </div>
              ),
            },
          ]}
        />

        <div css={tw`mt-[16px]`}>
          <Alert
            type={guideCopied ? 'success' : 'warning'}
            showIcon
            message={
              guideCopied
                ? '계정 안내 문구를 복사했어요. 이제 창을 닫을 수 있어요.'
                : '비밀번호는 이 창에서만 확인할 수 있어요. 계정 안내 문구를 복사해야 창을 닫을 수 있어요.'
            }
          />
        </div>

        <Button
          css={tw`mt-[16px]`}
          block
          type="primary"
          ghost={guideCopied}
          icon={guideCopied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={async () => {
            if (await copy(guide, '계정 안내 문구')) {
              setGuideCopied(true);
            } else {
              setGuideCopyFailed(true);
            }
          }}
        >
          {guideCopied
            ? '계정 안내 문구 다시 복사'
            : '계정 안내 문구 전체 복사'}
        </Button>

        {guideCopyFailed && !guideCopied && (
          // 클립보드를 못 쓰는 환경에서 닫지 못하고 갇히지 않도록 직접 복사 경로를 둔다
          <div css={tw`flex flex-col gap-[8px] mt-[12px]`}>
            <Input.TextArea readOnly autoSize value={guide} />
            <Button block onClick={() => setGuideCopied(true)}>
              위 문구를 직접 복사했어요
            </Button>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      width={560}
      maskClosable={false}
      onCancel={handleClose}
      title="유저 생성"
      okText="생성"
      cancelText="취소"
      confirmLoading={addAdminUser.isPending}
      onOk={() => form.submit()}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        css={tw`mt-[16px]`}
        onFinish={(values) => {
          addAdminUser.mutate({
            ...values,
            email: values.email.trim(),
            name: values.name.trim(),
            // 전체 지점 접근 역할이면 지점 매핑은 의미가 없지만, 고른 값이 있으면 그대로 저장한다
            branchIds: values.branchIds ?? [],
          });
        }}
      >
        <Form.Item<FormValues> label="구분" name="userType" required>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={(Object.keys(USER_TYPE) as (keyof typeof USER_TYPE)[]).map(
              (value) => ({ value, label: USER_TYPE[value] })
            )}
          />
        </Form.Item>

        <div css={tw`grid grid-cols-2 gap-x-[16px]`}>
          <Form.Item<FormValues>
            label="이름"
            name="name"
            rules={[
              {
                required: true,
                whitespace: true,
                message: '이름을 입력해주세요',
              },
              { max: 50, message: '50자 이내로 입력해주세요' },
            ]}
          >
            <Input placeholder="ex) 김다락" maxLength={50} />
          </Form.Item>

          <Form.Item<FormValues>
            label="휴대폰 번호"
            name="phone"
            normalize={formatPhone}
            rules={[
              { required: true, message: '휴대폰 번호를 입력해주세요' },
              {
                pattern: PHONE_PATTERN,
                message: '010-1234-5678 형태로 입력해주세요',
              },
            ]}
          >
            <Input placeholder="010-1234-5678" inputMode="numeric" />
          </Form.Item>
        </div>

        <Form.Item<FormValues>
          label="이메일 (로그인 ID)"
          name="email"
          rules={[
            { required: true, message: '이메일을 입력해주세요' },
            { type: 'email', message: '이메일 형식이 아니에요' },
          ]}
        >
          <Input placeholder="partner@example.com" autoComplete="off" />
        </Form.Item>

        <Form.Item<FormValues>
          label="임시 비밀번호"
          required
          extra="자동 생성된 비밀번호예요. 생성 후 파트너에게 전달해주세요."
        >
          <Space.Compact css={tw`w-full`}>
            <Form.Item<FormValues>
              name="password"
              noStyle
              rules={[{ required: true, message: '비밀번호를 생성해주세요' }]}
            >
              <Input readOnly css={tw`font-mono`} />
            </Form.Item>
            <Tooltip title="다시 생성">
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  form.setFieldValue('password', generatePassword())
                }
              />
            </Tooltip>
            <Tooltip title="복사">
              <Button
                icon={<CopyOutlined />}
                disabled={!password}
                onClick={() => copy(password, '비밀번호')}
              />
            </Tooltip>
          </Space.Compact>
        </Form.Item>

        <div css={tw`grid grid-cols-2 gap-x-[16px]`}>
          <Form.Item<FormValues>
            label="역할"
            name="userRoleId"
            rules={[{ required: true, message: '역할을 선택해주세요' }]}
          >
            <Select
              placeholder="역할 선택"
              loading={roles.isFetching}
              options={roleOptions}
              optionRender={({ data }) => (
                <div css={tw`flex flex-row items-center justify-between`}>
                  <span>{data.label}</span>
                  <Tag css={tw`m-0`}>
                    {USER_ROLE_TYPE[data.role.type] ?? data.role.type}
                  </Tag>
                </div>
              )}
            />
          </Form.Item>

          <Form.Item<FormValues> label="상태" name="userStatus" required>
            <Radio.Group
              options={(
                Object.keys(USER_STATUS) as (keyof typeof USER_STATUS)[]
              ).map((value) => ({ value, label: USER_STATUS[value] }))}
            />
          </Form.Item>
        </div>

        <Form.Item<FormValues>
          label="지점 권한"
          name="branchIds"
          extra={
            roleHasAllBranchAccess
              ? '선택한 역할은 전체 지점(ALL)에 접근해요. 지점을 고르지 않아도 돼요.'
              : '파트너가 조회할 수 있는 지점이에요.'
          }
          rules={[
            {
              validator: (_, value?: number[]) =>
                roleHasAllBranchAccess || (value && value.length > 0)
                  ? Promise.resolve()
                  : Promise.reject(new Error('지점을 1개 이상 선택해주세요')),
            },
          ]}
          dependencies={['userRoleId']}
        >
          <BranchTagSelect />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
